/**
 * ReceiptsPage — `/recibos`: os comprovantes de pagamento da empresa.
 *
 * Módulo GRÁTIS, sem gate de plano nem de papel (o PRO só tira o rodapé de
 * marca do PDF exportado).
 *
 * ─── O DESENHO OFICIAL NUMA LISTA DE COMPROVANTES ───────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/clients`, `/catalog`,
 * `/trabalhos` e `/ordens-servico`. O que precisou ser TRADUZIDO aqui (o
 * exercício do #182/#188/#195/#197/#198):
 *
 * · O EYEBROW diz o "onde" — a família de que a lista faz parte.
 * · O VALOR AO VIVO é a soma do recorte visível, e a regra de quem entra nela é
 *   de NEGÓCIO: recibo CANCELADO não comprova pagamento nenhum e fica FORA, com
 *   a contagem do que ficou de fora escrita na sublinha ("no silent caps").
 *   Mora no módulo puro `receiptsListFilters`.
 * · O BOTÃO DE VOLTAR fica, como em `/ordens-servico`: `/recibos` NÃO é destino
 *   da navegação inferior, então sem ele não há saída barata da tela.
 * · O ESTADO virou faixa de botões planos (Todos/Emitidos/Cancelados) — são
 *   três e não cinco porque o recibo não tem máquina de estados (#57), e um
 *   quarto botão seria um recorte garantidamente vazio (#91).
 * · A SELEÇÃO virou um MODO (#192): a caixa em todo card cobrava de quem só veio
 *   consultar o preço da exportação, que é ação de exceção. A exportação em
 *   massa segue sendo a única ação em massa da tela (sem excluir e sem duplicar:
 *   excluir comprovante em massa é arriscado demais para um toque, e duplicar
 *   criaria N recibos datados de hoje sem ninguém conferir um por um).
 *
 * ⚠️ O QUE NÃO PODE REGREDIR AQUI: `receipts` não tem coluna `status` (#57) e
 * `issue_date` é DATE (#114). As duas leituras passam pelo módulo puro; ler
 * `r.status` compila e devolve `false` para sempre, e `new Date(issue_date)`
 * imprime o DIA ANTERIOR em UTC-3 — num comprovante, um dia a menos é um
 * documento errado.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  ArrowUpDown,
  Check,
  FileDown,
  Loader2,
  Plus,
  ReceiptText,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { BulkActionBar } from '@/components/bulk-actions/BulkActionBar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useFeature } from '@/hooks/useFeature';
import { buildReceiptsPdf, receiptsExportFilename } from '@/lib/receiptsBulkExport';
import { savePdfDocument } from '@/lib/pdfGenerator';
import { todayIsoDate } from '@/lib/financeStatus';
import { plural } from '@/lib/plural';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useReceipts } from '@/hooks/useReceipts';
import {
  DEFAULT_RECEIPT_SORT,
  RECEIPT_PERIOD_PRESETS,
  RECEIPT_SORT_LABELS,
  RECEIPT_SORT_ORDER,
  RECEIPT_STATUS_FILTER_OPTIONS,
  countActiveReceiptFilters,
  countReceiptsByStatus,
  isDeadReceiptStatusFilter,
  matchesReceiptPeriod,
  matchesReceiptStatus,
  matchesReceiptValue,
  receiptIsCancelled,
  receiptListTotalLabel,
  receiptMatchesSearch,
  sortReceiptsBy,
  summarizeReceiptList,
  type ReceiptSortOption,
  type ReceiptStatusFilter,
} from '@/lib/receiptsListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { PERIOD_PRESET_LABELS, resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cn } from '@/lib/utils';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/**
 * 'YYYY-MM-DD' → dd/mm/yyyy sem drift de fuso.
 *
 * `issue_date` é DATE: `new Date(iso)` parseia como meia-noite UTC e em UTC-3
 * imprime o DIA ANTERIOR (#114). A data de um recibo tem valor probatório.
 */
const formatDate = (d?: string | null) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('T')[0].split('-');
  return day && m && y ? `${day}/${m}/${y}` : String(d);
};

const PERIOD_GROUP_KEY = 'period';

export default function ReceiptsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const queryClient = useQueryClient();

  const [exporting, setExporting] = useState(false);
  const noBrandingFooter = useFeature('no_branding_footer');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReceiptStatusFilter>('all');
  const [sortOption, setSortOption] = useState<ReceiptSortOption>(DEFAULT_RECEIPT_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  /**
   * MODO DE SELEÇÃO — desligado por padrão (#192). Só depois de o usuário dizer
   * que quer selecionar é que as caixas nascem nos cards.
   */
  const [selectionMode, setSelectionMode] = useState(false);

  const { receipts, isLoading } = useReceipts();

  // Refetch when an offline write (or another tab) touches receipt data.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['receipts', activeCompany?.id] });
    };
    window.addEventListener('receipts-updated', handler);
    return () => window.removeEventListener('receipts-updated', handler);
  }, [queryClient, activeCompany?.id]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e chamá-la
   * por recibo compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM o estado — é ele que alimenta as contagens dos botões. */
  const receiptsBeforeStatus = useMemo(
    () =>
      (receipts ?? []).filter(
        (receipt: any) =>
          receiptMatchesSearch(receipt, search) &&
          matchesReceiptPeriod(receipt, periodRange) &&
          matchesReceiptValue(receipt, valueRange),
      ),
    [receipts, search, periodRange, valueRange],
  );

  const statusCounts = useMemo(
    () => countReceiptsByStatus(receiptsBeforeStatus),
    [receiptsBeforeStatus],
  );

  const visible = useMemo(
    () =>
      sortReceiptsBy(
        receiptsBeforeStatus.filter((receipt: any) => matchesReceiptStatus(receipt, statusFilter)),
        sortOption,
      ),
    [receiptsBeforeStatus, statusFilter, sortOption],
  );

  /**
   * `includeDead` liga quando o recorte É o estado morto: com o filtro em
   * "Cancelados", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeReceiptList(visible, {
    includeDead: isDeadReceiptStatusFilter(statusFilter),
  });
  const activeModalFilters = countActiveReceiptFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || statusFilter !== 'all' || activeModalFilters > 0;

  const {
    selectedCount,
    allSelected,
    isSelected,
    toggleItem,
    clearSelection,
    toggleSelectAll,
    selectedItems,
  } = useBulkSelection(visible as { id: string }[]);

  // Limpa a seleção quando o recorte muda. A ORDENAÇÃO fica de fora de
  // propósito: ela reorganiza as mesmas linhas, então a seleção continua válida.
  useEffect(() => {
    clearSelection();
  }, [search, statusFilter, periodPreset, valueRange, clearSelection]);

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Emitido em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...RECEIPT_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  /**
   * Exporta os recibos selecionados num PDF único, um por página.
   *
   * Recibos ainda NÃO SINCRONIZADOS entram normalmente: o PDF é montado a
   * partir dos dados em mão, sem servidor nenhum, e para quem está fechando o
   * mês no campo justamente esses são os que faltam.
   */
  const handleExport = async () => {
    if (selectedItems.length === 0) return;
    setExporting(true);
    try {
      const doc = await buildReceiptsPdf(selectedItems as any[], {
        company: activeCompany as any,
        isPremium: noBrandingFooter.allowed,
      });
      await savePdfDocument(
        doc,
        receiptsExportFilename(selectedItems.length, todayIsoDate()),
        { context: 'receipts-bulk-export' },
      );
      toast.success(
        `${plural(selectedItems.length, 'recibo exportado', 'recibos exportados')}.`,
      );
      clearSelection();
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível exportar os recibos.');
    } finally {
      setExporting(false);
    }
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ReceiptText className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Recibos</h2>
          <p className="mb-4 text-muted-foreground">Faça login para acessar os recibos</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideFab={selectedCount > 0}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/recibos` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Documentos</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Recibos</h1>
            </div>
            {/* A ação PRIMÁRIA mantém o texto — é ela que o excedente comeria
                primeiro se tudo fosse texto, e é a que menos pode sumir (#186). */}
            <Button3D
              size="sm"
              color="light"
              className="shrink-0"
              onClick={() => navigate('/recibos/novo')}
              aria-label="Novo recibo"
              title="Novo recibo"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Novo
            </Button3D>
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{receiptListTotalLabel(statusFilter)}</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): escrever uma regra local aqui seria mais uma cópia de
                uma medida que já divergiu entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatCurrency(summary.total)),
              )}
            >
              {formatCurrency(summary.total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {/* Enquanto a lista carrega, o hero NÃO afirma que está vazia: um
                  "Nenhum recibo" que aparece antes da resposta é uma afirmação
                  falsa em cima de um R$ 0,00 igualmente falso. */}
              {summary.count === 0
                ? isLoading
                  ? 'Carregando…'
                  : 'Nenhum recibo neste filtro'
                : plural(summary.count, 'recibo', 'recibos')}
              {/* "no silent caps": o que ficou fora da soma é DITO. Sem isto o
                  total apareceria menor do que a lista sugere, sem explicação. */}
              {summary.excludedCount > 0 &&
                ` · ${plural(
                  summary.excludedCount,
                  'cancelado fora da conta',
                  'cancelados fora da conta',
                )}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e seleção. Eles são um
              grupo só (todos respondem "o que eu estou vendo"), então ficam
              juntos com `space-y-2` em vez do `space-y-5` que separa as SEÇÕES
              da tela (#191/#194). */}
          <div className="space-y-2">
            {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o
                campo é `min-w-0 flex-1`: sem isso o input empurraria os botões
                para fora da tela em vez de encolher (#186). */}
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Pagador, descrição ou #0001"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ordenação: menu, não modal — é uma escolha única entre rótulos
                  curtos, e um diálogo seria dois cliques a mais pelo mesmo
                  resultado. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${RECEIPT_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${RECEIPT_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {RECEIPT_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                          placeholder, o rótulo escolhido fica deslocado. */}
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {RECEIPT_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Filtros: aqui SIM um modal — são controles independentes que
                  fazem sentido escolhidos juntos, e o rascunho do diálogo é o
                  que impede a lista de se reorganizar a cada tecla do campo de
                  valor. */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* ESTADO — botões planos, não alternador (#25/#188). A contagem vai
                no `title`/`aria-label`, onde não custa pixel (#191). */}
            <div className={FILTER_CHIP_ROW}>
              {RECEIPT_STATUS_FILTER_OPTIONS.map((option) => {
                const active = statusFilter === option.value;
                // A contagem responde "quantos eu veria se clicasse aqui" — por
                // isso sai do recorte SEM o estado. Um número que não bate com a
                // lista depois do clique é pior do que número nenhum.
                const count = statusCounts[option.value] ?? 0;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    aria-pressed={active}
                    aria-label={`${option.label} (${count})`}
                    title={`${option.label}: ${plural(count, 'recibo', 'recibos')}`}
                    className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Entrada e saída do MODO DE SELEÇÃO (#192).
                Fora dele a esquerda mostra a ORDENAÇÃO ativa e a direita o botão
                "Selecionar"; dentro, a ordenação dá lugar à marcação de todos e
                o botão vira a SAÍDA. Sair limpa a seleção: deixar itens marcados
                sem nenhuma caixa na tela deixaria a `BulkActionBar` falando de
                uma escolha invisível. */}
            {!isLoading && visible.length > 0 && (
              <div className="flex items-center justify-between gap-2 px-1">
                {selectionMode ? (
                  /* `<label>` em volta do Checkbox do Radix alterna DUAS vezes e
                     vira no-op (#29) — o padrão do app é o par Checkbox + área
                     de texto com `onClick` próprio. */
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(!!checked, visible as { id: string }[])
                      }
                      aria-label="Selecionar todos os recibos"
                    />
                    <span
                      className="cursor-pointer text-sm text-muted-foreground"
                      onClick={() => toggleSelectAll(!allSelected, visible as { id: string }[])}
                    >
                      Selecionar todos ({visible.length})
                    </span>
                  </div>
                ) : (
                  <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                    <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{RECEIPT_SORT_LABELS[sortOption]}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (selectionMode) clearSelection();
                    setSelectionMode((prev) => !prev);
                  }}
                  className="shrink-0 rounded-full px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  {selectionMode ? 'Concluir' : 'Selecionar'}
                </button>
              </div>
            )}

            {/* Lista — DENTRO do mesmo grupo `space-y-2` dos controles, e não um
                `-mt-N` competindo com o `space-y-5` de fora: margem negativa num
                filho direto PERDE a briga de especificidade contra o `space-y-*`
                do container (#194). */}
            {isLoading && visible.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={cn(CONTENT_CARD, 'h-24 animate-pulse')} />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <ReceiptText className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="font-medium">
                  {recorteAtivo ? 'Nenhum recibo encontrado' : 'Nenhum recibo'}
                </p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  {recorteAtivo
                    ? 'Nenhum recibo corresponde à busca e aos filtros escolhidos.'
                    : 'Emita um recibo de pagamento a partir de um orçamento, de uma ordem de serviço, de um lançamento do financeiro — ou do zero.'}
                </p>
                {!recorteAtivo && (
                  <Button3D className="mt-5" onClick={() => navigate('/recibos/novo')}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Novo recibo
                  </Button3D>
                )}
              </div>
            ) : (
              /* Duas colunas no desktop (padrão de `/quotes`), com
                 `grid-cols-1` explícito no celular (#185). */
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {visible.map((r: any) => {
                  const cancelled = receiptIsCancelled(r);
                  const selected = isSelected(r.id);
                  return (
                    <div
                      key={r.id}
                      className={cn(
                        CONTENT_CARD,
                        'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                        selected && 'ring-2 ring-primary',
                      )}
                      onClick={() => navigate(`/recibos/${r.id}`)}
                    >
                      {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é
                          quem dita a altura do card — o `items-center` do card
                          faz o bloco valor+estado centrar contra ELA, e não
                          contra a linha do pagador (#189). */}
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* LINHA DE IDENTIFICAÇÃO: seleção, número e data. Tudo
                            que identifica o recibo (mas não é o conteúdo dele)
                            mora aqui, o que devolve a largura inteira ao nome do
                            pagador na linha de baixo. */}
                        <div className="flex flex-wrap items-center gap-2">
                          {selectionMode && (
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleItem(r.id)}
                              // O card inteiro navega; o clique na seleção não
                              // pode subir, senão marcar um recibo abriria a
                              // tela dele.
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Selecionar recibo de ${r.payer_name || 'pagador'}`}
                            />
                          )}
                          {r._offline_pending && (
                            <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px]">
                              Offline
                            </Badge>
                          )}
                          {r.show_sequence_id && r.sequence_id && (
                            <Badge
                              variant="outline"
                              className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                            >
                              #{String(r.sequence_id).padStart(4, '0')}
                            </Badge>
                          )}
                          {r.issue_date && (
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                            >
                              {formatDate(r.issue_date)}
                            </Badge>
                          )}
                        </div>

                        {/* `min-w-0` não é decoração (#175): sem ele um flex
                            item nasce com `min-width: auto` e um nome sem
                            espaços estica a linha para fora da viewport. E sem
                            `truncate`: é o pagador que distingue duas linhas da
                            mesma lista, e cortá-lo come justamente o que
                            informa. */}
                        <div className="min-w-0 space-y-0.5">
                          <p
                            className={cn(
                              'min-w-0 break-words font-bold',
                              cancelled && 'text-muted-foreground line-through',
                            )}
                          >
                            {r.payer_name || 'Recibo'}
                          </p>
                          {r.description && (
                            <p className="break-words text-sm text-muted-foreground">
                              {r.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Valor + estado são UM CONJUNTO, e por isso um bloco só:
                          centrados entre si no eixo horizontal e centrados na
                          ALTURA DO CARD pelo `items-center` do card (#189). */}
                      <div className="flex shrink-0 flex-col items-center gap-1">
                        <p className="font-semibold">{formatCurrency(Number(r.amount ?? 0))}</p>
                        {/* O selo sai do MESMO predicado da soma e da faixa
                            (#57): `receipts` não tem coluna `status`, e um
                            leitor que se apoie nela deixa passar recibo
                            cancelado como válido. */}
                        <Badge
                          className={cn(
                            'border-0',
                            cancelled
                              ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
                          )}
                        >
                          {cancelled ? 'Cancelado' : 'Emitido'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reusa a pílula de ações em massa das outras listas. Sem excluir e sem
          duplicar aqui: excluir em massa comprovante de pagamento é arriscado
          demais para um toque, e duplicar vários criaria N recibos datados de
          hoje sem ninguém conferir um por um. */}
      <BulkActionBar
        selectedCount={selectedCount}
        totalCount={visible.length}
        allSelected={allSelected}
        onSelectAll={(checked) => toggleSelectAll(checked, visible as { id: string }[])}
        onClear={clearSelection}
        extraAction={{
          label: 'Exportar',
          icon: exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4" />
          ),
          onClick: () => void handleExport(),
        }}
      />

      {/* Filtros: quando o recibo foi emitido e a faixa de valor.
          O ESTADO aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela — repeti-lo no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte os recibos por data de emissão e por valor."
        groups={filterGroups}
        rangeLabel="Valor do recibo"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && RECEIPT_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
    </MainLayout>
  );
}
